import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { MyLine } from '#/shared/api/generated/communication/models'
import { useSetDefaultNumberApiV1TelephonyNumbersPhoneIdDefaultPost } from '#/shared/api/generated/communication/theCargoCommunication'
import { invalidateMyLines } from '#/shared/lib/telephony'
import { toast } from '#/shared/ui/sonner'

import { resolveActiveLine, useActiveLineStore } from './active-line'
import { useMyLines } from './use-my-lines'
import { outboundCapableLines, outboundLines } from '../lib/line-capabilities'
import type { LineCapability } from '../lib/line-capabilities'

export interface UseDefaultLineOptions {
  /**
   * Restrict the lines to those that can perform this outbound action. Omit to
   * include every outbound-capable line (call or text). Inbound is unaffected —
   * every line receives calls regardless of which is the default.
   */
  capability?: LineCapability
  /** Defer the `GET /telephony/numbers/mine` fetch until the UI is opened. */
  enabled?: boolean
}

export interface UseDefaultLineResult {
  /** Eligible outbound lines, default line first. */
  lines: MyLine[]
  /** The resolved default outbound line, or null until lines load. */
  line: MyLine | null
  /**
   * Persist `id` as the account default (`POST /telephony/numbers/{id}/default`)
   * and make it the active outbound line everywhere once the backend accepts it.
   */
  setDefault: (id: string) => void
  /** Id of the line whose default is being persisted, or null. */
  pendingId: string | null
  /** True when more than one eligible line exists — drives the switcher. */
  hasMultiple: boolean
  isLoading: boolean
  /** The lines fetch has completed (see {@link MyLinesResult.loaded}). */
  loaded: boolean
}

/**
 * The single source of truth for the default **outbound** phone line. Every
 * place that originates a call or text reads the resolved {@link line} from
 * here, and the two surfaces that let the user change it — the phone-panel
 * header switcher and the telephony connection dialog — both call
 * {@link setDefault}, which persists the account default. Read-only surfaces
 * (e.g. detail-sidebar call/SMS buttons) consume the line but never change it.
 *
 * Inbound is out of scope: all lines ring regardless of the default.
 */
export function useDefaultLine(
  options: UseDefaultLineOptions = {},
): UseDefaultLineResult {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    lines: myLines,
    isLoading,
    loaded,
  } = useMyLines({ enabled: options.enabled })
  const selectedId = useActiveLineStore((state) => state.selectedId)
  const setSelected = useActiveLineStore((state) => state.select)

  const lines = options.capability
    ? outboundLines(myLines, options.capability)
    : outboundCapableLines(myLines)
  const line = resolveActiveLine(lines, selectedId)

  const setDefaultMutation =
    useSetDefaultNumberApiV1TelephonyNumbersPhoneIdDefaultPost()
  const pendingId = setDefaultMutation.isPending
    ? setDefaultMutation.variables.phoneId
    : null

  const setDefault = useCallback(
    (id: string) => {
      // Persist the pick first; only move the active line once the backend
      // accepts it, so the selection never lands on a line we couldn't set.
      setDefaultMutation.mutate(
        { phoneId: id },
        {
          onSuccess: () => {
            setSelected(id)
            invalidateMyLines(queryClient)
          },
          onError: () => {
            toast.error(t('telephony.toast.defaultFailed'))
          },
        },
      )
    },
    [setDefaultMutation, setSelected, queryClient, t],
  )

  return {
    lines,
    line,
    setDefault,
    pendingId,
    hasMultiple: lines.length > 1,
    isLoading,
    loaded,
  }
}
